import { useState, useEffect } from 'react';

const HISTORY_KEY = 'token_search_history';
const MAX_HISTORY = 8;

export interface SearchHistoryItem {
  address: string;
  timestamp: number;
  symbol?: string;
  imageUrl?: string | null;
}

export function useSearchHistory() {
  const [history, setHistory] = useState<SearchHistoryItem[]>([]);

  // Load search history from localStorage on mount
  useEffect(() => {
    const stored = localStorage.getItem(HISTORY_KEY);
    if (stored) {
      try {
        const parsed = JSON.parse(stored);
        setHistory(parsed);
      } catch (e) {
        console.error('Failed to parse search history', e);
      }
    }
  }, []);

  // Save search to history (call after API success with optional symbol/image)
  const saveToHistory = (
    address: string,
    options?: { symbol?: string; imageUrl?: string | null }
  ) => {
    const entry: SearchHistoryItem = {
      address,
      timestamp: Date.now(),
      ...(options?.symbol != null && { symbol: options.symbol }),
      ...(options?.imageUrl != null && { imageUrl: options.imageUrl }),
    };
    const newHistory = [
      entry,
      ...history.filter((item) => item.address !== address),
    ].slice(0, MAX_HISTORY);

    setHistory(newHistory);
    localStorage.setItem(HISTORY_KEY, JSON.stringify(newHistory));
  };

  // Delete from history
  const deleteFromHistory = (address: string) => {
    const newHistory = history.filter((item) => item.address !== address);
    setHistory(newHistory);
    localStorage.setItem(HISTORY_KEY, JSON.stringify(newHistory));
  };

  // Clear all history
  const clearHistory = () => {
    setHistory([]);
    localStorage.removeItem(HISTORY_KEY);
  };

  return {
    history,
    saveToHistory,
    deleteFromHistory,
    clearHistory,
  };
}
